import { hasPrivacyOptOutSignal } from "./privacyControls";
import { getFunnelConceptFromValue } from "./funnelConcept";

/**
 * A deliberately small, reporting-only snapshot of the landing URL. Keep this list in sync with
 * the checkout Worker; never persist an arbitrary query string, click ID, or full page URL here.
 */
export type LandingAttribution = {
  landingConceptId?: string;
  creativeId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
};

export type WorkerLandingAttribution = {
  landing_concept_id?: string;
  creative_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
};

/**
 * The same small, allowlisted attribution snapshot used by the checkout Worker,
 * shaped for analytics event properties. This intentionally exposes no full URL,
 * click ID, email, or arbitrary query parameter.
 */
export type AnalyticsLandingAttribution = WorkerLandingAttribution;

const LANDING_ATTRIBUTION_STORAGE_KEY = "linkhiv:landing-attribution:v1";
const MAX_LANDING_CONCEPT_LENGTH = 96;
const MAX_CREATIVE_LENGTH = 128;
const MAX_UTM_SOURCE_OR_MEDIUM_LENGTH = 64;
const MAX_UTM_CAMPAIGN_OR_CONTENT_LENGTH = 128;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/;
const URL_LIKE_VALUE_PATTERN = /(?:[a-z][a-z\d+.-]*:\/\/|www\.)/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const normalizeValue = (value: unknown, maxLength: number): string | undefined => {
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  if (!trimmed || CONTROL_CHARACTER_PATTERN.test(trimmed) || URL_LIKE_VALUE_PATTERN.test(trimmed)) {
    return undefined;
  }

  return trimmed.slice(0, maxLength);
};

/**
 * Normalizes the only six campaign values Linkhiv accepts. `utm_content` is retained as its own
 * field and also supplies `creativeId` when an explicit `creative_id` was not present.
 */
export const normalizeLandingAttribution = (value: unknown): LandingAttribution | undefined => {
  if (!isRecord(value)) return undefined;

  const utmContent = normalizeValue(value.utmContent, MAX_UTM_CAMPAIGN_OR_CONTENT_LENGTH);
  const suppliedConceptId = normalizeValue(value.landingConceptId, MAX_LANDING_CONCEPT_LENGTH);
  const resolvedConcept = getFunnelConceptFromValue(suppliedConceptId);
  const attribution: LandingAttribution = {
    // Never record an arbitrary `concept` query parameter as if the matching message were
    // actually rendered. Valid aliases collapse to the canonical funnel concept key.
    landingConceptId: resolvedConcept?.key,
    creativeId:
      normalizeValue(value.creativeId, MAX_CREATIVE_LENGTH) ??
      normalizeValue(value.utmContent, MAX_CREATIVE_LENGTH),
    utmSource: normalizeValue(value.utmSource, MAX_UTM_SOURCE_OR_MEDIUM_LENGTH),
    utmMedium: normalizeValue(value.utmMedium, MAX_UTM_SOURCE_OR_MEDIUM_LENGTH),
    utmCampaign: normalizeValue(value.utmCampaign, MAX_UTM_CAMPAIGN_OR_CONTENT_LENGTH),
    utmContent
  };

  return Object.values(attribution).some(Boolean) ? attribution : undefined;
};

/** Reads an allowlisted campaign snapshot from a landing URL without retaining the URL itself. */
export const getLandingAttributionFromLocation = (
  locationLike: Pick<Location, "href"> | URL
): LandingAttribution | undefined => {
  if (hasPrivacyOptOutSignal()) return undefined;

  const url = locationLike instanceof URL ? locationLike : new URL(locationLike.href);
  const params = url.searchParams;

  return normalizeLandingAttribution({
    landingConceptId: params.get("concept"),
    creativeId: params.get("creative_id"),
    utmSource: params.get("utm_source"),
    utmMedium: params.get("utm_medium"),
    utmCampaign: params.get("utm_campaign"),
    utmContent: params.get("utm_content")
  });
};

const readStoredLandingAttribution = (): LandingAttribution | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const raw = window.sessionStorage.getItem(LANDING_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return undefined;

    return normalizeLandingAttribution(JSON.parse(raw));
  } catch {
    return undefined;
  }
};

const clearStoredLandingAttribution = (): void => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(LANDING_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in private mode or blocked by the browser.
  }
};

const storeLandingAttribution = (attribution: LandingAttribution): void => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(LANDING_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Attribution is optional reporting data, so continue when session storage is unavailable.
  }
};

/**
 * Returns the current landing campaign snapshot for this tab. A newly supplied allowlisted
 * landing URL supersedes the old snapshot; hash-route changes and reloads without campaign
 * parameters reuse the session value. Opted-out visitors never retain or return attribution.
 */
export const getOrCreateLandingAttribution = (): LandingAttribution | undefined => {
  if (typeof window === "undefined") return undefined;
  if (hasPrivacyOptOutSignal()) {
    clearStoredLandingAttribution();
    return undefined;
  }

  const fromLocation = getLandingAttributionFromLocation(window.location);
  if (fromLocation) {
    storeLandingAttribution(fromLocation);
    return fromLocation;
  }

  return readStoredLandingAttribution();
};

/** Converts the frontend-friendly shape to the explicit, snake_case Worker request contract. */
export const toWorkerLandingAttribution = (
  attribution: LandingAttribution | undefined
): WorkerLandingAttribution | undefined => {
  if (hasPrivacyOptOutSignal()) return undefined;

  const normalized = normalizeLandingAttribution(attribution);
  if (!normalized) return undefined;

  const workerAttribution: WorkerLandingAttribution = {
    landing_concept_id: normalized.landingConceptId,
    creative_id: normalized.creativeId,
    utm_source: normalized.utmSource,
    utm_medium: normalized.utmMedium,
    utm_campaign: normalized.utmCampaign,
    utm_content: normalized.utmContent
  };

  return Object.values(workerAttribution).some(Boolean) ? workerAttribution : undefined;
};

/**
 * Keeps product-analytics events comparable with checkout and payment records.
 * It is deliberately an alias of the Worker shape so a `creative_id` has the
 * same meaning at every point in the funnel.
 */
export const toAnalyticsLandingAttribution = (
  attribution: LandingAttribution | undefined
): AnalyticsLandingAttribution | undefined => toWorkerLandingAttribution(attribution);
